import type { PayMethodQuoteModel, WalletModel } from '@/models';
import type { Asset, CryptoCurrency, Currency, PayMethod } from '@/types';
import { ModelView } from '@/types';
import { shrinkWallet } from '@/utilities/wallet';
import Decimal from 'decimal.js';
import { getDecimalsForFormattedCryptoPrecision } from "@/helpers/getDecimalsForFormattedCryptoPrecision";

const SHRUNKEN_WALLET_ADDRESS_LENGTH = 32;

interface Option {
    id: string;
    currency: Currency;
    displayCurrency?: string;
    name: string;
}

export class BaseOptionModel<T extends Option = Option> {
    private readonly _displayCurrency: string;

    constructor(readonly item: T) {
        this._displayCurrency = item.displayCurrency ?? item.currency;
    }

    get id() {
        return this.item.id;
    }

    get currency() {
        return this.item.currency;
    }

    get displayCurrency() {
        return this._displayCurrency;
    }

    get name() {
        return this.item.name;
    }
}

export class FiatOptionModel extends BaseOptionModel {
    constructor(
        readonly method: PayMethodModel,
    ) {
        super({ id: method.id, currency: method.currency, name: method.name });
    }

    get icon() {
        return this.method.icon;
    }

    get fees() {
        return this.method.fees;
    }

    get labels() {
        return this.method.labels;
    }

    get description() {
        const currencySymbols: Record<Currency, string> = {
            ['EUR' as Currency]: '€',
            ['USD' as Currency]: '$',
            ['GBP' as Currency]: '£',
        };

        const amountFormatter = new Intl.NumberFormat(undefined, { style: 'decimal' });

        const minAmountCurrency = currencySymbols[this.method.minAmount.currencyCode] ?? this.method.minAmount.currencyCode + ' ';
        const minAmount = `${ minAmountCurrency }${ amountFormatter.format(Number(this.method.minAmount.roundedAmount)) }`;

        const maxAmountCurrency = currencySymbols[this.method.maxAmount.currencyCode] ?? this.method.maxAmount.currencyCode + ' ';
        const maxAmount = `${ maxAmountCurrency }${ amountFormatter.format(Number(this.method.maxAmount.roundedAmount)) }`;

        return `min ${ minAmount } – max ${ maxAmount }`;
    }

    get relationLink() {
        return this.method.relationLink;
    }
}

export class CryptoOptionModel extends BaseOptionModel {
    constructor(readonly method: CryptoCurrency) {
        super(method);
    }

    get currency() {
        return this.method.asset;
    }

    get icon() {
        return this.method.icon;
    }

    get assetId() {
        return this.method.asset;
    }
}

interface Balance {
    amount: string;
    currency: Currency;
}

export class BalanceModel extends ModelView<Balance> {
    get amount() {
        return this.item.amount;
    }

    get currency() {
        return this.item.currency;
    }
}

export class WalletOptionModel extends BaseOptionModel {
    constructor(readonly method: WalletModel, readonly asset: Asset, readonly fiat: BalanceModel, readonly defaultOptionName: string) {
        super({ id: method.assetId, currency: method.assetId, displayCurrency: asset.currency, name: defaultOptionName });
    }

    get assetId() {
        return this.id;
    }

    get customName() {
        return this.method.name;
    }

    get isEmpty() {
        return this.method.isEmpty;
    }

    get assetName() {
        return this.asset.name;
    }

    get icon() {
        return this.asset.currency.toLowerCase();
    }

    get addresses() {
        return this.method.addresses;
    }

    get primaryAddress() {
        return this.method.addresses.find(address => address.primaryAddress)?.address ?? '';
    }

    get shrunkenPrimaryAddress() {
        return shrinkWallet(this.primaryAddress, SHRUNKEN_WALLET_ADDRESS_LENGTH);
    }

    get hasPositiveBalance(): boolean {
        return parseFloat(this.method.balance.amount) > 0;
    }

    get balance() {
        return this.method.balance;
    }

    get minimumAllowedBalance() {
        const { currency } = this.balance;
        const listMinimumLimit: { [key: string]: number } = {
            XRP: 1,
            DOT: 1,
            XLM: 1,
        };

        return listMinimumLimit[currency] || 0;
    }

    get maxSendBalance() {
        const { amount } = this.balance;

        const maxSendBalance = new Decimal(amount).minus(this.minimumAllowedBalance);
        const maxDecimals = getDecimalsForFormattedCryptoPrecision(this.currency);

        return new Decimal(maxSendBalance.toFixed(maxDecimals, Decimal.ROUND_FLOOR));
    }

    get fiatBalance() {
        return this.fiat;
    }

    get formattedCryptoBalance() {
        let amount = this.balance.amount;
        const dotPosition = amount.indexOf('.');
        if (dotPosition !== -1) {
            amount = amount.substring(0, dotPosition + 9);
        }
        return `${ amount } ${ this.balance.currency }`;
    }
}

export class PayMethodModel extends ModelView<PayMethod> {
    currency: Currency;

    constructor(item: PayMethod, private readonly _currency: Currency) {
        super(item);
        this.currency = _currency;
    }

    get id() {
        return this.item.id;
    }

    get name() {
        return this.item.name;
    }

    get icon() {
        return this.item.icon;
    }

    get fees() {
        return this.item.fees;
    }

    get labels() {
        return this.item.labels;
    }

    get minAmount() {
        return this.item.minAmount;
    }

    get maxAmount() {
        return this.item.maxAmount;
    }

    get relationLink() {
        return this.item.link;
    }
}

export class CryptoMethodModel extends ModelView<CryptoCurrency> {
    get currency() {
        return this.item.currency;
    }

    get name() {
        return this.item.name;
    }

    get icon() {
        return this.item.icon;
    }
}

export class FiatMethodWithQuote {
    constructor(
        private readonly payMethod: PayMethodModel,
        private readonly quoteItem: PayMethodQuoteModel | null = null,
    ) {
    }

    get id() {
        return this.payMethod.id;
    }

    get name() {
        return this.payMethod.name;
    }

    get icon() {
        return this.payMethod.icon;
    }

    get labels() {
        return this.payMethod.labels;
    }

    get minAmount() {
        return this.payMethod.minAmount;
    }

    get maxAmount() {
        return this.payMethod.maxAmount;
    }

    get relationLink() {
        return this.payMethod.relationLink;
    }

    get quote() {
        return this.quoteItem;
    }
}
